import React, { useEffect, useRef, useState } from "react";
import { uniqBy } from "lodash";
import { StylesConfig } from "react-select";
import { formatCoordinates } from "common/utils/coordinates";
import { getValueForParcelFieldNameOverride } from "common-client/utils/parcels";
import { colors } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { ActionButton } from "../../Common/Buttons/ActionButton";
import { Icon } from "../../Common/Icons/LucideIcons";
import { InformationalTooltip } from "../../Common/Tooltip";
import { Body } from "../../Common/Typography";
import { Select } from "../../Inputs";
import { AddressPanelParcel, AddressPanelProperty } from "../types";
import {
  Address,
  AddressSelector,
  ButtonRow,
  CoordinatesContainer,
  Header,
  ObjectInfoContainer,
  ObjectInfoRow,
  PrivatedInfo,
} from "./__styles__/Header";
import PropertyOptions from "./PropertyOptions";
import { SharePropertyInfoButton } from "./SharePropertyInfoButton";
import { useSharePropertyInfoModal } from "./SharePropertyModal";
import { HeaderParcel } from "./types";

const MultiplePropertiesTooltip = () => {
  return (
    <div>
      <InformationalTooltip
        tooltipText="Warning: There are multiple addresses within this parcel."
        place="bottom"
        iconProps={{ color: "contentSecondaryInverted" }}
        leftSpace={false}
      />
    </div>
  );
};

interface AddressSelectOption {
  value: string;
  label: string;
}

const getAddressOrCoords = (
  property: NonNullable<AddressPanelParcel>["properties"][0],
) => {
  return property.fullAddress ?? formatCoordinates(property);
};

interface AddressPanelHeaderProps {
  loading: boolean;
  property: Maybe<AddressPanelProperty>;
  properties: Array<NonNullable<AddressPanelParcel>["properties"][0]>;
  parcel: HeaderParcel;
  latitude: number;
  longitude: number;
  onClose: () => void;
  selectProperty: (
    property: NonNullable<AddressPanelParcel>["properties"][0],
  ) => void;
}

export default ({
  loading,
  property,
  properties,
  parcel,
  latitude,
  longitude,
  onClose,
  selectProperty,
}: AddressPanelHeaderProps) => {
  const { account } = React.useContext(AuthContext);

  let options = properties.map(property => {
    const label = getAddressOrCoords(property);
    return {
      value: property.id,
      label,
      // Despite the SelectOption type not including this in its definition,
      // we need to be able to access property data once the option is selected
      property,
    };
  });

  options = uniqBy(options, "value");

  const styles: StylesConfig<AddressSelectOption, false> = {
    option: (provided, state) => {
      const backgroundColor =
        state.isFocused ? colors.bgUiInteraction.toString() : undefined;
      return {
        ...provided,
        backgroundColor,
        color: colors.contentPrimary.toString(),
      };
    },
    control: provided => {
      const style = {
        backgroundColor: colors.grey600.toString(),
        borderStyle: undefined,
      };
      return { ...provided, ...style };
    },
    singleValue: provided => {
      return { ...provided, color: colors.contentPrimaryInverted.toString() };
    },
    indicatorsContainer: () => {
      return {};
    },
  };

  const showAddressSelect = options.length > 1;

  const [showSharePropertyModal, hideSharePropertyModal] =
    useSharePropertyInfoModal({
      onCancel: () => {
        hideSharePropertyModal();
      },
      onSubmit: () => {
        hideSharePropertyModal();
      },
      property,
    });

  const propertyHiddenFromPublic = !!property?.hiddenFromPublic;
  const parcelIdLabel = getValueForParcelFieldNameOverride({
    overrides: account?.parcelFieldNameOverrides,
    name: "parcelId",
  });

  const parcelIdRef = useRef<HTMLParagraphElement>(null);
  const coordinatesRef = useRef<HTMLDivElement>(null);
  const displayCoordinates = !!latitude && !!longitude;
  const [coordinatesWrapped, setCoordinatesWrapped] = useState(false);

  useEffect(() => {
    const checkWrap = () => {
      if (parcelIdRef.current && coordinatesRef.current) {
        setCoordinatesWrapped(
          parcelIdRef.current.offsetTop !== coordinatesRef.current.offsetTop,
        );
      }
    };

    checkWrap();
    window.addEventListener("resize", checkWrap);
    return () => window.removeEventListener("resize", checkWrap);
  }, []);

  return (
    <Header data-panel-header>
      <ObjectInfoRow>
        <ObjectInfoContainer>
          <Body
            size="small"
            type="regular"
            color="contentSecondaryInverted"
            ref={parcelIdRef}
          >
            {`${parcelIdLabel}: `}
            {parcel?.parcelNumber || "--"}
          </Body>
          {showAddressSelect && <MultiplePropertiesTooltip />}
          <CoordinatesContainer ref={coordinatesRef}>
            {displayCoordinates && (
              <Body
                size="small"
                type="regular"
                color="contentSecondaryInverted"
              >
                {!coordinatesWrapped && `• `}
                {`Coordinates: ${formatCoordinates({ latitude, longitude })}`}
              </Body>
            )}
          </CoordinatesContainer>
        </ObjectInfoContainer>
        <ActionButton
          icon="x"
          variant="inverted"
          onClick={onClose}
          data-panel-close
          aria-label="Close"
        />
      </ObjectInfoRow>
      <AddressSelector>
        {!loading && showAddressSelect && property ?
          <Select
            name="type"
            options={options}
            value={property.id}
            styles={styles}
            dropdownIndicatorColor="contentPrimaryInverted"
            onChange={selectedValue => {
              const selectedProperty = options.find(
                ({ value }) => value === selectedValue,
              )?.property;

              if (selectedProperty) {
                selectProperty(selectedProperty);
              }
            }}
          />
        : <Address>
            {loading ?
              <div>
                <em>Loading...</em>
              </div>
            : property && getAddressOrCoords(property)}
          </Address>
        }
      </AddressSelector>
      <ButtonRow data-testid="header-buttons">
        {property && account?.publicPortal.enabled && (
          <SharePropertyInfoButton
            onPress={() => {
              track("Share property info button clicked", {
                streetAddress: property.streetAddress,
                city: property.city,
              });
              showSharePropertyModal();
            }}
            disabled={propertyHiddenFromPublic}
          />
        )}
        {!loading && (
          <PropertyOptions
            latitude={latitude}
            longitude={longitude}
            parcel={parcel}
            property={property}
          />
        )}
      </ButtonRow>
      {propertyHiddenFromPublic && (
        <PrivatedInfo>
          <Icon iconName="eye-off" color="contentSecondaryInverted" size={16} />
          <div>Property hidden from public</div>
        </PrivatedInfo>
      )}
    </Header>
  );
};
